import { SELECTOR_TYPE } from '@/constants'
import { generateSelectorReference } from '@/employees/components/Selector/utilities/generateReference'
import store from '@/employees/store/index'
/** @typedef {('composite'|'not'|'subscribable'|'subscribable_grouping'|'group'|'mentor'|'student_characteristic')} SelectorTypeEnum */
/**
 * @typedef Selector
 * @property {string} [reference]
 * @property {SelectorTypeEnum} type
 */
// #region Type checking of a selector
/**
 * @param {Selector} selector
 * @returns { selector is CompositeSelector }
 */
export function isCompositeType (selector) {
  return selector.type === 'composite'
}
/**
 * @param {Selector} selector
 * @returns { selector is NotSelector }
 */
export function isNotType (selector) {
  return selector.type === 'not'
}
/**
 * @param {Selector} selector
 * @returns { selector is SubscribableSelector }
 */
export function isSubscribableType (selector) {
  return selector.type === 'subscribable'
}
/**
 * @param {Selector} selector
 * @returns { selector is SubscribableGroupingSelector }
 */
export function isSubscribableGroupingSelectorType (selector) {
  return selector.type === 'subscribable_grouping'
}
/**
 *
 * @param {Selector} selector
 * @returns { selector is GroupSelector }
 */
export function isGroupSelectorType (selector) {
  return selector.type === 'group'
}
/**
 *
 * @param {Selector} selector
 * @returns { selector is MentorSelector }
 */
export function isMentorSelectorType (selector) {
  return selector.type === 'mentor'
}
/**
 *
 * @param {Selector} selector
 * @returns { selector is StudentCharacteristicSelector }
 */
export function isStudentCharacteristicType (selector) {
  return selector.type === 'student_characteristic'
}
// #endregion

// #region Add selector
/**
 * @param {Selector} parentSelector
 * @param {string} reference
 * @param {Selector} selector
 * @param {SelectorTypeEnum} selectorType
 */
export function searchParentAddChildSelector (
  parentSelector,
  reference,
  selector,
  selectorType,
) {
  switch (parentSelector.type) {
    case SELECTOR_TYPE.composite :addSelectorToCompositeSelector(parentSelector, reference, selector, selectorType); break
    case SELECTOR_TYPE.not :addSelectorToNotSelector(parentSelector, reference, selector, selectorType); break
  }
}
/**
 * @param {Selector} parentSelector
 * @param {string} reference
 * @param {Selector} selector
 * @param {SelectorTypeEnum} selectorType
 */
function addSelectorToCompositeSelector (
  parentSelector,
  reference,
  selector,
  selectorType,
) {
  if (parentSelector.reference === reference && selectorType === SELECTOR_TYPE.composite) {
    parentSelector.selectors.push(selector)
  } else {
    for (let i = 0; i < parentSelector.selectors.length; i++) {
      searchParentAddChildSelector(parentSelector.selectors[i], reference, selector, selectorType)
    }
  }
}
/**
 * @param {Selector} parentSelector
 * @param {string} reference
 * @param {Selector} selector
 * @param {SelectorTypeEnum} selectorType
 */
function addSelectorToNotSelector (
  parentSelector,
  reference,
  selector,
  selectorType,
) {
  if (parentSelector.selector.reference === reference && selectorType === SELECTOR_TYPE.not) {
    parentSelector.selector = selector
  } else {
    searchParentAddChildSelector(parentSelector.selector, reference, selector, selectorType)
  }
}
// #endregion

// #region search selector add property
/**
 * Will add or update an property at location based on the reference and property
 *
 * @param {Selector} selector
 * @param {string} reference
 * @param {string} property
 * @param {any} value
 * @returns {void}
 */
export function addSelectorProperty (selector, reference, property, value) {
  if (selector.reference === reference) {
    selector[property] = value
  } else if (isCompositeType(selector) && selector.selectors) {
    for (let i = 0; i < selector.selectors.length; i++) {
      addSelectorProperty(selector.selectors[i], reference, property, value)
    }
  } else if (isNotType(selector)) {
    addSelectorProperty(selector.selector, reference, property, value)
  }
}
// #endregion

// #region Change selector operator
/**
 * @param {Selector} selectorRule
 * @param {string} reference
 * @param {'and'|'or'} operator
 * @param {SelectorTypeEnum} selectorType
 */
export function selectorOperatorChange (
  selectorRule,
  reference,
  operator,
  selectorType,
) {
  if (isCompositeType(selectorRule) && selectorRule.reference === reference) {
    selectorRule.operator = operator
  } else if (isSubscribableType(selectorRule) && selectorRule.reference === reference) {
    selectorRule.state = operator
  } else if (isCompositeType(selectorRule) && selectorRule.selectors) {
    for (let i = 0; i < selectorRule.selectors.length; i++) {
      selectorOperatorChange(selectorRule.selectors[i], reference, operator, selectorType)
    }
  } else if (isNotType(selectorRule)) {
    selectorOperatorChange(selectorRule.selector, reference, operator, selectorType)
  }
}
// #endregion

// #region
/**
 * @param {Selector} selector
 * @param {string} oldName
 * @param {string} newName
 */
export function selectorUpdateSelectorGroup (selector, oldName, newName) {
  if (isGroupSelectorType(selector) && selector.group === oldName) {
    selector.group = newName
  } else if (isCompositeType(selector) && selector.selectors) {
    for (let i = 0; i < selector.selectors.length; i++) {
      selectorUpdateSelectorGroup(selector.selectors[i], oldName, newName)
    }
  } else if (isNotType(selector)) {
    selectorUpdateSelectorGroup(selector.selector, oldName, newName)
  }
}
// #endregion

// #region Delete selector
/**
 * @param {Selector} selector
 * @param {string} reference
 */
export function deleteSelector (
  selector,
  reference,
) {
  if (isCompositeType(selector) && selector.selectors) {
    let spliceIndex = null
    for (let i = 0; i < selector.selectors.length; i++) {
      if (selector.selectors[i].reference === reference) {
        spliceIndex = i
        break
      } else if (isCompositeType(selector.selectors[i])) {
        deleteSelector(selector.selectors[i], reference)
        deleteDisplaySelector(reference)
      }
    }
    if (spliceIndex !== null) {
      selector.selectors.splice(spliceIndex, 1)
    }
  } else if (isNotType(selector)) {
    deleteSelector(selector.selector, reference)
  }
}
// #endregion

// #region Display rule
/** @param {string} reference */
export function setDisplaySelector (reference) {
  store.dispatch('set_display_selector_reference', { reference, display: true })
}
/** @param {string} reference */
export function deleteDisplaySelector (reference) {
  store.dispatch('delete_display_selector_reference', reference)
}
// #endregion

// #region Selector builder functions
/**
 * @typedef CompositeSelector
 * @property {'composite'} type
 * @property {string} [reference]
 * @property {[Selector]} selectors
 * @property {('and'|'or')} operator
 */
/** @returns {CompositeSelector} */
export function buildCompositeSelector () {
  const selectors = []
  const selector = {
    reference: generateSelectorReference(),
    type: 'composite',
    selectors,
    operator: 'and',
  }
  setDisplaySelector(selector.reference)
  return selector
}
/**
 * @typedef NotSelector
 * @property {'not'} type
 * @property {string} [reference]
 * @property {Selector} selector
 */
/**
 * @param {Selector} selector1
 * @returns {NotSelector}
*/
export function buildNotType (selector1) {
  const selector = {
    reference: generateSelectorReference(),
    type: 'not',
    selector: { ...selector1 },
  }
  setDisplaySelector(selector.reference)
  return selector
}
/**
 * @typedef SubscribableSelector
 * @property {'subscribable'} type
 * @property {string} [reference]
 * @property {number} [subscribable]
 * @property {('subscribed'|'unsubscribed')} [state]
 * @property {[number]} [answers]
 * @property {('or'|'and')} [answer_operator]
 */
/** @returns {SubscribableSelector} */
export function buildSubscribableSelectorType () {
  const selector = {
    reference: generateSelectorReference(),
    type: 'subscribable',
    subscribable: null,
  }
  return selector
}
/**
 * @typedef SubscribableGroupingSelector
 * @property {'subscribable_grouping'} type
 * @property {string} [reference]
 * @property {boolean} complied
 */
/** @returns {SubscribableGroupingSelector} */
function buildSubscribableGroupingSelectorType () {
  const selector = {
    reference: generateSelectorReference(),
    type: 'subscribable_grouping',
    complied: true,
  }
  return selector
}
/**
 * @typedef ConfigGroup
 * @property {'config'} type
 * @property {number} id
 * @property {number} [term]
 */
/**
 * @typedef DatabaseGroup
 * @property {'database'} type
 * @property {string} name
 */
/** @typedef {(DatabaseGroup | ConfigGroup)} Group */
/**
 * @typedef GroupSelector
 * @property {'subscribable_grouping'} type
 * @property {string} [reference]
 * @property {Group} group
 */
/** @returns {GroupSelector} */
function buildGroupSelectorType () {
  const selector = {
    reference: generateSelectorReference(),
    type: 'group',
    group: null,
  }
  return selector
}
/**
 * @typedef MentorSelector
 * @property {'mentor'} type
 * @property {string} [reference]
 * @property {number} [mentor]
 * @property {Date} [date]
 */
/** @returns {MentorSelector} */
function buildMentorSelectorType () {
  return {
    reference: generateSelectorReference(),
    type: 'mentor',
    date: null,
  }
}
/**
 * @typedef StudentCharacteristicSelector
 * @property {'student_characteristic'} type
 * @property {string} [reference]
 * @property {number} [student_characteristic]
 * @property {[string]} values
 */
/** @returns {StudentCharacteristicSelector} */
function buildStudentCharacteristicSelectorType () {
  const selector = {
    reference: generateSelectorReference(),
    type: 'student_characteristic',
    student_characteristic: null,
    values: [''],
  }
  return selector
}
/**
 * @param {SelectorTypeEnum} ruleEnum
 * @returns {Selector}
 */
export function buildSelector (ruleEnum) {
  switch (ruleEnum) {
    case SELECTOR_TYPE.composite:
      return buildCompositeSelector()
    case SELECTOR_TYPE.not:
      return buildNotType(buildSubscribableSelectorType())
    case SELECTOR_TYPE.subscribable:
      return buildSubscribableSelectorType()
    case SELECTOR_TYPE.subscribable_grouping:
      return buildSubscribableGroupingSelectorType()
    case SELECTOR_TYPE.group:
      return buildGroupSelectorType()
    case SELECTOR_TYPE.mentor:
      return buildMentorSelectorType()
    case SELECTOR_TYPE.student_characteristic:
      return buildStudentCharacteristicSelectorType()
    default:
      throw new Error('This selector type either does not exist or is not implemented yet in the buildSelector function')
  }
}
// #endregion
